import { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MagnifyingGlass, FileText, LinkSimple, X, ArrowRight } from '@phosphor-icons/react';
import { knowledgeApi } from './knowledgeApi';

// Batch 6 · iter_156 — LinkPickerDialog. Two tabs:
//   (1) 記事 (Knowledge article) — authorised search, `data-kb-article-id`
//       is stored so links survive rename / relocation.
//   (2) URL — plain external URL with client-side protocol validation.
// When editing an existing link, the dialog opens on whichever tab matches
// the current mark's `data-kb-article-id` presence.
//
// Article search uses the existing GET /api/knowledge/search endpoint,
// which already permission-filters results (`_can_view`). Parent-path is
// resolved client-side by joining the search hits with the sidebar tree
// data supplied by the parent (via `tree` prop) — no new backend endpoint
// needed.
export default function LinkPickerDialog({ open, onClose, onSave, onRemove, initial, tree = [] }) {
  const isEdit = !!initial?.href;
  const initialInternal = !!initial?.articleId;
  const [tab, setTab] = useState(initialInternal ? 'article' : 'url');
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(
    initialInternal ? { id: initial.articleId, title: initial.text || '' } : null,
  );
  const [url, setUrl] = useState(initialInternal ? '' : (initial?.href || ''));
  const [urlErr, setUrlErr] = useState('');
  const searchAbort = useRef(null);
  const debounce = useRef(null);

  useEffect(() => {
    if (!open) return;
    setTab(initialInternal ? 'article' : 'url');
    setQ('');
    setResults([]);
    setSelectedArticle(initialInternal ? { id: initial.articleId, title: initial.text || '' } : null);
    setUrl(initialInternal ? '' : (initial?.href || ''));
    setUrlErr('');
  }, [open, initialInternal, initial]);

  // Debounced article search — fires 200 ms after last keystroke.
  useEffect(() => {
    if (tab !== 'article' || !open) return undefined;
    if (debounce.current) clearTimeout(debounce.current);
    if (!q.trim()) { setResults([]); return undefined; }
    debounce.current = setTimeout(async () => {
      setSearching(true);
      if (searchAbort.current) searchAbort.current.abort?.();
      try {
        const hits = await knowledgeApi.search(q.trim());
        setResults(hits || []);
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 200);
    return () => { if (debounce.current) clearTimeout(debounce.current); };
  }, [q, tab, open]);

  // Build a fast id → { title, parentPath } lookup from the sidebar tree.
  const nodeIndex = useMemo(() => {
    const idx = new Map();
    const walk = (nodes, ancestors) => {
      (nodes || []).forEach((n) => {
        idx.set(n.id, { title: n.title, parentPath: ancestors });
        if (n.children?.length) walk(n.children, [...ancestors, n.title || '無題']);
      });
    };
    walk(tree, []);
    return idx;
  }, [tree]);

  const validateUrl = () => {
    const v = url.trim();
    if (!v) { setUrlErr('URLを入力してください'); return null; }
    // reject unsafe protocols outright
    if (/^\s*(javascript|data|vbscript):/i.test(v)) { setUrlErr('このURLは使用できません'); return null; }
    if (/^(https?:\/\/|mailto:|\/)/i.test(v)) { setUrlErr(''); return v; }
    // bare domain shortcut
    if (/^[\w-]+(\.[\w-]+)+/i.test(v) && !v.includes(' ')) { setUrlErr(''); return `https://${v}`; }
    setUrlErr('http(s):// または mailto: で始まるURLを入力してください');
    return null;
  };

  const commit = () => {
    if (tab === 'article') {
      if (!selectedArticle) return;
      onSave?.({
        href: `/knowledge/p/${selectedArticle.id}`,
        articleId: selectedArticle.id,
        text: selectedArticle.title,
        target: '_blank',
      });
      onClose?.();
      return;
    }
    const safe = validateUrl();
    if (!safe) return;
    onSave?.({ href: safe, articleId: null, target: '_blank' });
    onClose?.();
  };

  const removeLink = () => { onRemove?.(); onClose?.(); };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg" aria-describedby={undefined} data-testid="kb-link-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">{isEdit ? 'リンクを編集' : 'リンクを挿入'}</DialogTitle>
        </DialogHeader>

        {/* Tab strip */}
        <div className="flex gap-1 border-b border-zinc-200">
          <button type="button"
            onClick={() => setTab('article')}
            data-testid="kb-link-tab-article"
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === 'article' ? 'border-blue-600 text-blue-700' : 'border-transparent text-zinc-500 hover:text-zinc-800'
            }`}>
            <FileText size={14} weight="duotone" /> 記事
          </button>
          <button type="button"
            onClick={() => setTab('url')}
            data-testid="kb-link-tab-url"
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === 'url' ? 'border-blue-600 text-blue-700' : 'border-transparent text-zinc-500 hover:text-zinc-800'
            }`}>
            <LinkSimple size={14} /> URL
          </button>
        </div>

        {/* Article tab */}
        {tab === 'article' && (
          <div className="space-y-3">
            <div className="relative">
              <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input
                autoFocus
                data-testid="kb-link-article-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="タイトルで検索…"
                className="h-10 pl-8"
              />
            </div>
            {selectedArticle && (
              <div className="rounded-md border border-blue-200 bg-blue-50 px-2 py-1.5 text-xs text-blue-800 flex items-center gap-1.5" data-testid="kb-link-article-selected">
                <FileText size={12} weight="fill" /> {selectedArticle.title}
                <button type="button" className="ml-auto text-blue-500 hover:text-blue-700"
                  onClick={() => setSelectedArticle(null)}
                  data-testid="kb-link-article-clear">
                  <X size={12} weight="bold" />
                </button>
              </div>
            )}
            <div className="max-h-64 overflow-y-auto rounded-md border border-zinc-100" data-testid="kb-link-article-results">
              {searching && (
                <div className="p-3 text-xs text-zinc-400" data-testid="kb-link-article-loading">検索中…</div>
              )}
              {!searching && q.trim() && results.length === 0 && (
                <div className="p-3 text-xs text-zinc-400" data-testid="kb-link-article-empty">該当する記事はありません</div>
              )}
              {!searching && results.map((r) => {
                const info = nodeIndex.get(r.id);
                const pathTxt = info?.parentPath?.length ? info.parentPath.join(' / ') : '';
                const isDraft = r.status === 'draft';
                return (
                  <button key={r.id} type="button"
                    onClick={() => setSelectedArticle({ id: r.id, title: r.title || '無題のページ' })}
                    data-testid={`kb-link-article-hit-${r.id}`}
                    className={`w-full flex items-start gap-2 px-3 py-2 text-left text-sm hover:bg-zinc-50 border-b last:border-b-0 border-zinc-100 ${
                      selectedArticle?.id === r.id ? 'bg-blue-50/60' : ''
                    }`}>
                    <FileText size={14} weight="duotone" className="mt-0.5 text-zinc-400 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate font-medium text-zinc-800">{r.title || '無題のページ'}</span>
                        {isDraft && <span className="shrink-0 rounded bg-amber-100 text-amber-700 text-[10px] font-medium px-1 py-0.5">下書き</span>}
                      </div>
                      {pathTxt && <div className="truncate text-[11px] text-zinc-400">{pathTxt}</div>}
                    </div>
                    <ArrowRight size={12} weight="bold" className="mt-1 text-zinc-300 shrink-0" />
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* URL tab */}
        {tab === 'url' && (
          <div className="space-y-2">
            <label className="text-xs font-medium text-zinc-600">URL</label>
            <Input
              autoFocus
              data-testid="kb-link-url-input"
              value={url}
              onChange={(e) => { setUrl(e.target.value); if (urlErr) setUrlErr(''); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } }}
              placeholder="https://example.com または mailto:you@example.com"
              className="h-10"
            />
            {urlErr && <p className="text-xs text-rose-600" data-testid="kb-link-url-error">{urlErr}</p>}
            <p className="text-[11px] text-zinc-400">http(s)://, mailto:, または /path のみ挿入できます。</p>
          </div>
        )}

        <DialogFooter className="gap-2">
          {isEdit && (
            <Button type="button" variant="outline" onClick={removeLink} data-testid="kb-link-remove-btn" className="mr-auto text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700">
              リンクを削除
            </Button>
          )}
          <Button type="button" variant="outline" onClick={onClose} data-testid="kb-link-cancel-btn">キャンセル</Button>
          <Button type="button"
            onClick={commit}
            disabled={tab === 'article' ? !selectedArticle : !url.trim()}
            data-testid="kb-link-save-btn"
            className="bg-teal-700 hover:bg-teal-800">
            {isEdit ? '更新' : '挿入'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
